import { useState, useRef, useEffect } from 'react';
import { Button } from '@shared/components';
import { useDropUp } from '@shared/hooks/useDropUp';

interface Props {
  index: number;
  total: number;
  onInsertAbove: () => void;
  onSwapUp: () => void;
  onSwapDown: () => void;
  onDeleteMultiple: (count: number) => void;
  onDeleteRow: () => void;
}

const itemCls =
  'block w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed';

/** 見積明細行の「操作」メニュー（行の挿入・並べ替え・複数行消去・この行を削除）。 */
export function QuoteItemOpMenu({
  index, total, onInsertAbove, onSwapUp, onSwapDown, onDeleteMultiple, onDeleteRow,
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { menuRef, dropUp } = useDropUp(open);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const run = (fn: () => void) => { setOpen(false); fn(); };

  const handleMulti = () => {
    const input = window.prompt('指定行を含む消去行数を半角で入力して下さい', '1');
    if (input === null) return;
    const count = parseInt(input.trim(), 10);
    if (Number.isNaN(count) || count < 1) return;
    onDeleteMultiple(count);
  };

  return (
    <div ref={ref} className="relative inline-block text-left">
      <Button type="button" variant="primary" size="sm" onClick={() => setOpen((v) => !v)}>
        操作
      </Button>
      {open && (
        <div
          ref={menuRef}
          className={`absolute right-0 ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'} z-50 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1`}
        >
          <button type="button" className={itemCls} onClick={() => run(onInsertAbove)}>
            ↩ 行の挿入
          </button>
          <div className="my-1 border-t border-gray-100" />
          <button type="button" className={itemCls} disabled={index === 0} onClick={() => run(onSwapUp)}>
            ↑ 上の行と入替
          </button>
          <button type="button" className={itemCls} disabled={index >= total - 1} onClick={() => run(onSwapDown)}>
            ↓ 下の行と入替
          </button>
          <div className="my-1 border-t border-gray-100" />
          <button type="button" className={itemCls} onClick={() => run(handleMulti)}>
            ≫ 複数行消去
          </button>
          <button type="button" className={itemCls + ' text-red-600'} onClick={() => run(onDeleteRow)}>
            🗑 この行を削除
          </button>
        </div>
      )}
    </div>
  );
}
